// @component
//   @param {Array} props.events - The list of events to display in the day view. Each event should have `title` and `start_time` properties.
//   @param {Date} props.currentDay - The currently selected day to display the calendar.

//   @example
//  // Example usage of DayView component

//   import React, { useState } from 'react';
//   import DayView from './DayView';

//   const events = [
//       { 
//           title: 'Team Meeting', 
//           start_time: '2024-12-19T10:00:00' // Event at 10:00 AM 
//      },
//       { 
//           title: 'Client Call', 
//           start_time: '2024-12-19T14:30:00' // Event at 2:30 PM
//       },
//   ];

//   function App() {
//       const [selectedDay, setSelectedDay] = useState(new Date()); // Default to current day

//       return (
//          <div>
//              <h1>Day View Calendar</h1>
//              <DayView 
//                  events={events} // Pass the list of events
//                  currentDay={selectedDay} // Pass the selected day
//              />
//          </div>
//      );
//  }

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { readanCreatedEvent } from '../../Redux/Actions/Actions';
import { useNavigate } from 'react-router-dom';
import { formatDateView } from './ReusableFunctions';

export default function DayView({ events = [], currentDay, clickDate, allTasks, handleEdit, setSelectedTableDate, selectedDay }) {

    const [tasks, setTasks] = useState([]);
    const [selectedDate, setSelectedDate] = useState();
    const timeSlotRefs = useRef([]);
    const getEventList = useSelector(state => state.getEventList)
    const dispatch = useDispatch();
    const [selectedEventCategory, setSelectedEventCategory] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (getEventList) {
            const data = getEventList[0] ? getEventList[0] : [];
            const tasks_keys = Object.keys(data);
            const taskData = {};

            tasks_keys?.forEach((key) => {
                const taskList = data[key];
                taskList?.forEach((task) => {

                    const { task_start_time } = task;
                    const task_date_time_array = task_start_time?.split(" ");
                    const task_time = task_date_time_array[1]
                    const task_date = task_date_time_array[0]
                    const finalTask = {
                        ...task,
                        category_name: key,
                        start_time: task_time,
                    };
                    if (task_date && taskData[task_date]) {
                        taskData[task_date].push(finalTask);
                    } else {
                        taskData[task_date] = [finalTask];
                    }
                })

            })
            setTasks(taskData);
        }
    }, [getEventList]);

    useEffect(() => {
        if (allTasks) {
            const facilityId = localStorage.getItem("selected_facility_id")
            const startDate = selectedDate
            const endDate = startDate + " 23:59:59";

            dispatch(readanCreatedEvent(facilityId, selectedEventCategory, startDate, endDate))

        }
    }, [selectedDate])
    // Memoized safeCurrentDay to handle invalid dates
    const safeCurrentDay = useMemo(() => {
        const date = new Date(clickDate).toLocaleDateString('en-CA');
        setSelectedDate(date);
        return currentDay instanceof Date && !isNaN(clickDate) ? new Date(clickDate) : new Date(clickDate);
    }, [clickDate]);

    // Memoized time slots to avoid unnecessary recalculations
    const timeSlots = useMemo(() => {
        const isSameDay = (date1, date2) => {
            return (
                date1.getDate() === date2.getDate() &&
                date1.getMonth() === date2.getMonth() &&
                date1.getFullYear() === date2.getFullYear()
            );
        };

        return Array.from({ length: 24 }, (_, i) => {
            const hour = i % 12 === 0 ? 12 : i % 12; // 12-hour format
            const period = i < 12 ? 'AM' : 'PM';
            const timeLabel = `${hour}:00 ${period}`;

            const slotEvents = events.filter(event => {
                const eventDate = new Date(event.start_time);
                return eventDate.getHours() === i && isSameDay(eventDate, safeCurrentDay);
            });

            return { time: timeLabel, events: slotEvents };
        });
    }, [events, safeCurrentDay]);

    const getEventColor = (eventName) => {
        switch (eventName) {
            case 'Maintenance':
                return '#E9C46A';
            case 'AMC':
                return '#2A9D8F';
            case 'Daily Checklist':
                return '#F4A261';
            case 'PPM':
                return '#264653';
            default:
                return '#E76F51';
        }
    };

    useEffect(() => {
        const currentHour = new Date().getHours();
        if (timeSlotRefs.current[currentHour]) {
            timeSlotRefs.current[currentHour].scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [timeSlots]);

    return (
        <div className="fin_man_day_calendar_container">
            <div className="d-flex day_calendar_top_header">
                <div className="time"></div>
                <div className="task d-flex justify-content-center fs-5">
                    {safeCurrentDay.toLocaleDateString('en-IN', { weekday: 'long' })},{' '}
                    {safeCurrentDay.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
            </div>

            {timeSlots?.map((slot, index) => {

                const now = new Date();
                const currentHour = now.getHours();
                const minutesNow = now.getMinutes();

                const isCurrentHour = index === currentHour; //calculating if this slot is in the current hour

                // const timelabel = slot.time;
                const taskSlots = {};

                for (let i = 0; i < 24; i++) {
                    taskSlots[i] = []; // Initialize an empty array for each hour
                }

                const tasksList = tasks[selectedDate];
                if (tasksList?.length > 0) {
                    tasksList?.forEach(task => {
                        const taskStartTime = new Date(`1970-01-01T${task?.start_time}`);
                        const taskHour = taskStartTime.getHours();

                        if (!isNaN(taskHour)) {
                            taskSlots[taskHour].push(task);
                        }
                    });
                }

                const dateCheck = (dateToCheck) => {
                    const [year, month, day] = dateToCheck?.split('-')?.map(Number);
                    const formattedDate = new Date(year, month - 1, day);
                    const today = new Date();

                    if (formattedDate <= today) {
                        return false;
                    } else {
                        return true;
                    }
                }



                return (
                    <div
                        className="d-flex"
                        key={index}
                        ref={el => (timeSlotRefs.current[index] = el)}
                        style={{ position: 'relative' }}
                    >
                        <div className="time">{slot.time}</div>
                        <div className={slot?.events?.length > 0 ? 'task' : 'task-add1'}>
                            {taskSlots[index]?.map((task, i) => (
                                <div
                                    key={i}
                                    className="task-item1 C-pointer"
                                    style={{
                                        backgroundColor: getEventColor(task.category_name),
                                    }}
                                >
                                    <div className="task-content">
                                        <div className="task-title">{task.task_name}</div>
                                        <div className="task-time">{task?.start_time}</div>
                                    </div>

                                    <div className="task-icons">
                                        <img
                                            src="./images/icons/viewhite.svg"
                                            alt="View"
                                            className="view-icon"
                                            // style={{ cursor: 'pointer', width: '20px', height: '20px', color: 'white' }}
                                            onClick={() => {
                                                const startTime = task?.task_start_time?.split(" ")[0];
                                                const taskData = {
                                                    event_name: task?.task_name,
                                                    event_id: task.event_id,
                                                    category_name: task.category_name,
                                                    event_category_id: task.event_category_id,
                                                    event_startTime: task?.start_time,
                                                    asset_name: task?.entity_name,
                                                    entity_type: task?.entity_type,
                                                    event_startDate: startTime,
                                                    event_endDate: startTime,
                                                };
                                                navigate('/FacilityManagerWorkLog', { state: { event: taskData } });
                                            }}
                                        />
                                        {dateCheck(selectedDate) &&
                                            <img src="./images/icons/White.svg" alt="Edit" className="edit-icon"
                                                onClick={() => {
                                                    const date = formatDateView(clickDate)
                                                    setSelectedTableDate(date)
                                                    const startDate = task?.task_start_time?.split(" ")[0];
                                                    task.startDate = startDate;
                                                    const taskData = {
                                                        ...task,
                                                        startDate: startDate,
                                                    }
                                                    handleEdit(taskData)
                                                }}
                                            />
                                        }
                                    </div>
                                </div>

                            ))}
                            {/* Red line for the current time */}
                            {isCurrentHour && (
                                <div
                                    className="hour-line"
                                    style={{
                                        top: `calc(${(minutesNow / 60) * 100}%)`,
                                        // position: 'absolute',
                                        height: '2px',
                                        width: '86%',
                                        backgroundColor: 'red',
                                        zIndex: 10,
                                        marginLeft: '-20px'
                                    }}
                                />
                            )}

                        </div>
                    </div>
                );
            })}
        </div>
    );
}